import React, { useState } from 'react';
import { parse, stringify } from 'query-string';
import PropTypes from 'prop-types';

import {
  bookingShape,
  currencyShape,
  historyShape,
  legShape,
  locationShape,
  stationsShape,
  userShape,
  productShape
} from 'constants/propTypes';

import serviceTypeCodes from 'constants/serviceTypeCodes';

import { getAOPStatus } from '../ExchangeResult.utils';

import ExchangeJourneySearch from 'components/queries/ExchangeJourneySearch';
import useAnimalsPerTrainsQuery from 'components/queries/useAnimalsPerTrainsQuery';
import useAssistancePerTrainsQuery from 'components/queries/useAssistancePerTrainsQuery';
import { Row } from 'components/shared/Layout';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { isOneWayExchange, isTwoWayExchange } from 'utils/exchange';
import { removeEmptyValues } from 'utils/functions';
import { isBookingMultiJourney, isUserEurostarAgent } from 'utils/booking';
import useServiceDetailsQuery from 'components/queries/useServiceDetailsQuery';
import {
  isSegmentASummatedFare,
  isSegmentAThroughFare,
  selectedItemsAreInASummatedFare,
  selectedItemsAreInAThroughFare,
  selectedItemsAreInADirectFare,
  isExchangingASubsetOfPassengers,
  buildFaresBeingExchanged
} from 'utils/summatedFares';
import config from 'utils/config';
import { OUTBOUND } from 'constants/booking';
import {
  PUBLIC_NON_FLEX_PRODUCTS,
  CORP_NON_FLEX_PRODUCTS
} from 'pages/BookingDetails/Journeys/AfterSalesCtas/ImpulseUpgradeCta/impulseUpgrade.utils';
import SearchRequestBookingMagnet from 'components/shared/SearchRequestBookingMagnet';

import ExchangeResults from '../ExchangeResults';

import { THALYS_OFFICES } from 'types/constants';
import { SearchContainer, StyledModal } from './ExchangeJourneySelection.styles';
import {
  getStationUic,
  buildInboundInfo,
  buildOutboundInfo,
  buildStationsForSearch,
  flattenProductsByLeg,
  getReasonsForDisablingSwapStations
} from './ExchangeJourneySelection.utils';
import { INTENTION_TYPE } from 'constants/search';
import { bookingStatus } from 'types/booking';
import { types } from 'types/passengers';

const ExchangeJourneySelection = ({
  reference,
  overrideReason,
  exchangeCurrency,
  booking,
  history,
  location,
  outbound,
  inbound,
  stations,
  user,
  outboundTrainNumbers,
  inboundTrainNumbers,
  membershipNumber,
  isMember,
  activePoints,
  hasSpecialLoyaltyTier = false,
  showBookingMagnet,
  setShowBookingMagnet,
  outboundExtras,
  inboundExtras,
  isMultiJourneyBooking,
  bookingItemRefs,
  outboundLegs,
  inboundLegs
}) => {
  const [animals, { loading: animalsLoading, data: animalsData }] = useAnimalsPerTrainsQuery();
  const [assistance, { loading: assistanceLoading, data: assistanceData }] = useAssistancePerTrainsQuery();
  const [serviceDetails, { loading: serviceDetailsLoading, data: serviceDetailsData }] = useServiceDetailsQuery();
  const enableShowSummatedFares = config('ENABLE_SHOW_SUMMATED_FARES', { canOverride: true }) === 'true';
  const [outboundSelectedFare, setOutboundSelectedFare] = useState(null);
  const [inboundSelectedFare, setInboundSelectedFare] = useState(null);

  const { status, productFamilies, passengers, additionalDetails } = booking;

  const isOnHoldOrProvisionalBooking = [bookingStatus.TEMPORARILY_ON_HOLD, bookingStatus.ON_HOLD, bookingStatus.PROVISIONAL].includes(
    status
  );

  const isOutboundSummatedFare = isSegmentASummatedFare(booking.outbound);
  const isInboundSummatedFare = isSegmentASummatedFare(booking.inbound);
  const isOutboundThroughFare = isSegmentAThroughFare(booking.outbound);
  const isInboundThroughFare = isSegmentAThroughFare(booking.inbound);
  const isExchangingASubsetOfPassengersOutbound = isExchangingASubsetOfPassengers(booking.outbound, bookingItemRefs);
  const isExchangingASubsetOfPassengersInbound = isExchangingASubsetOfPassengers(booking.inbound, bookingItemRefs);
  const isExchangingMultipleLegsOfAOutboundSummated =
    selectedItemsAreInASummatedFare(booking.outbound, bookingItemRefs, isOutboundSummatedFare) && outboundLegs.length > 1;
  const isExchangingMultipleLegsOfAInboundSummated =
    selectedItemsAreInASummatedFare(booking.inbound, bookingItemRefs, isInboundSummatedFare) && inboundLegs.length > 1;
  const isExchangingSinglegOfAOutboundSummated =
    selectedItemsAreInASummatedFare(booking.outbound, bookingItemRefs, isOutboundSummatedFare) && outboundLegs.length === 1;
  const isExchangingSingleLegOfAInboundSummated =
    selectedItemsAreInASummatedFare(booking.inbound, bookingItemRefs, isInboundSummatedFare) && inboundLegs.length === 1;
  const isExchangingOutboundThroughFare = selectedItemsAreInAThroughFare(booking.outbound, bookingItemRefs, isOutboundThroughFare);
  const isExchangingInboundThroughFare = selectedItemsAreInAThroughFare(booking.inbound, bookingItemRefs, isInboundThroughFare);
  const isExchangingOutboundDirectFare = selectedItemsAreInADirectFare(booking.outbound, bookingItemRefs);
  const isExchangingInboundDirectFare = selectedItemsAreInADirectFare(booking.inbound, bookingItemRefs);
  const outboundOrigin = isExchangingSinglegOfAOutboundSummated ? outbound.origin : booking.outboundOrigin;
  const outboundDestination = isExchangingSinglegOfAOutboundSummated ? outbound.destination : booking.outboundDestination;
  const inboundOrigin = isExchangingSingleLegOfAInboundSummated ? inbound.origin : booking.inboundOrigin;
  const inboundDestination = isExchangingSingleLegOfAInboundSummated ? inbound.destination : booking.inboundDestination;

  const summatedFares = {
    outbound: {
      isSingleLegExchange: isExchangingSinglegOfAOutboundSummated,
      isSummatedFare: isExchangingSinglegOfAOutboundSummated || isExchangingMultipleLegsOfAOutboundSummated
    },
    inbound: {
      isSingleLegExchange: isExchangingSingleLegOfAInboundSummated,
      isSummatedFare: isExchangingSingleLegOfAInboundSummated || isExchangingMultipleLegsOfAInboundSummated
    }
  };

  const isGroupBooking = productFamilies.some((pf) => pf.familyGroup === productFamilyCodes.GROUP);
  const isMultiJourney = isBookingMultiJourney(additionalDetails);

  const { outboundDate, inboundDate, bookingItems, swapStations, subscriptionStartDate, subscriptionEndDate } = parse(location.search);
  const isTwoWayJourneyOneWayExchange = outboundOrigin && inboundOrigin && !isTwoWayExchange({ outboundDate, inboundDate });

  const canSwapStations =
    !isGroupBooking &&
    isUserEurostarAgent(user) &&
    !isTwoWayJourneyOneWayExchange &&
    !(
      (outbound.bookingItems.length && outbound.bookingItems.length !== passengers.length) ||
      (inbound.bookingItems.length && inbound.bookingItems.length !== passengers.length)
    );

  const bookingStations = [];

  if (booking.outboundOrigin) {
    bookingStations.push(booking.outboundOrigin);
  }

  if (booking.outboundDestination) {
    bookingStations.push(booking.outboundDestination);
  }

  if (booking.inboundOrigin) {
    bookingStations.push(booking.inboundOrigin);
  }

  if (booking.inboundDestination) {
    bookingStations.push(booking.inboundDestination);
  }

  const shouldForceOutbound =
    !(isExchangingSinglegOfAOutboundSummated || isExchangingMultipleLegsOfAOutboundSummated) &&
    !isMultiJourney &&
    outbound.bookingItems.length > 0 &&
    outbound.bookingItems.length !== passengers.length;
  const shouldForceInbound =
    !(isExchangingSingleLegOfAInboundSummated || isExchangingMultipleLegsOfAInboundSummated) &&
    !isMultiJourney &&
    inbound.bookingItems.length > 0 &&
    inbound.bookingItems.length !== passengers.length;

  const hasSwapStations = canSwapStations && swapStations === 'true';

  const isOverrideRule = overrideReason !== undefined && !!overrideReason;

  const exchangeItems = bookingItems ? bookingItems.split(',').map(Number) : [];

  const { origin, destination, returningOrigin, returningDestination } = buildStationsForSearch({
    outboundDate,
    inboundDate,
    outboundOrigin,
    outboundDestination,
    inboundOrigin,
    inboundDestination
  });

  const outboundInfo = buildOutboundInfo({
    outboundDate,
    outboundOrigin,
    outboundDestination
  });

  const inboundInfo = buildInboundInfo({
    inboundDate,
    inboundOrigin,
    inboundDestination
  });

  const outboundExchangedItems = flattenProductsByLeg(booking?.outbound, exchangeItems);
  const inboundExchangedItems = flattenProductsByLeg(booking?.inbound, exchangeItems);

  const bookingProductFamily = booking?.productFamilyGroup;

  const isPubOrCorporateExchange = bookingProductFamily === productFamilyCodes.PUB || bookingProductFamily === productFamilyCodes.CORPORATE;

  const isBPNonFlexOutboundExchange =
    isPubOrCorporateExchange &&
    (bookingProductFamily === productFamilyCodes.PUB
      ? PUBLIC_NON_FLEX_PRODUCTS.some((product) => outboundExchangedItems?.includes(product))
      : CORP_NON_FLEX_PRODUCTS.some((product) => outboundExchangedItems?.includes(product)));

  const isBPNonFlexInboundExchange =
    isPubOrCorporateExchange &&
    (bookingProductFamily === productFamilyCodes.PUB
      ? PUBLIC_NON_FLEX_PRODUCTS.some((product) => inboundExchangedItems?.includes(product))
      : CORP_NON_FLEX_PRODUCTS.some((product) => inboundExchangedItems?.includes(product)));

  const searchArgs = removeEmptyValues({
    reference,
    origin: hasSwapStations ? getStationUic(destination) : getStationUic(origin),
    destination: hasSwapStations ? getStationUic(origin) : getStationUic(destination),
    returningOrigin: hasSwapStations ? getStationUic(returningDestination) : getStationUic(returningOrigin),
    returningDestination: hasSwapStations ? getStationUic(returningOrigin) : getStationUic(returningDestination),
    outboundDate,
    inboundDate,
    exchangeItems,
    currency: exchangeCurrency,
    isSwapStations: hasSwapStations,
    isBPNonFlexOutboundExchange,
    isBPNonFlexInboundExchange,
    summatedFares,
    multipleFlexibility: true,
    applySummatedFaresMaxTransferRules: true,
    ...(isGroupBooking && { maxTransfers: 0 }),
    ...(!isOnHoldOrProvisionalBooking && !hasSwapStations && { intention: INTENTION_TYPE.REBOOK }),
    offerSummatedFares: enableShowSummatedFares
  });

  const handleSelectFare = (fare) => {
    if (fare.direction === OUTBOUND) {
      setOutboundSelectedFare(fare);
    } else {
      setInboundSelectedFare(fare);
    }
  };

  const handleCompletedSearch = async (exchangeSearch) => {
    setOutboundSelectedFare(null);
    setInboundSelectedFare(null);

    const exchangeData = exchangeSearch.exchangeSearch || exchangeSearch.journeySearchForExchange;

    await animals({
      variables: {
        search: exchangeData,
        outboundDate,
        inboundDate,
        hasDogs: exchangeData.passengers?.some(({ type }) => type === types.DOG)
      }
    });

    await assistance({
      variables: {
        search: exchangeData,
        outboundDate,
        inboundDate
      }
    });

    await serviceDetails({
      variables: {
        search: exchangeData,
        outboundDate,
        inboundDate,
        origin: getStationUic(origin),
        destination: getStationUic(destination),
        isGroupSearch: isGroupBooking
      }
    });
  };

  const toggleSearchMenu = () => {
    setShowBookingMagnet(!showBookingMagnet);
  };

  const handleSearchChange = (searchData) => {
    const { travelDate, returnDate, hasSwapStations } = searchData;

    setShowBookingMagnet(false);

    const existingQuery = parse(location.search);
    const outboundDate = existingQuery.outboundDate && travelDate;
    const inboundDate = (outboundDate && returnDate) || (existingQuery.inboundDate && travelDate);

    const searchParams = removeEmptyValues({
      ...existingQuery,
      outboundDate,
      inboundDate,
      membershipNumber,
      swapStations: hasSwapStations
    });

    history.push({
      pathname: `/exchange/${reference}`,
      search: stringify(searchParams)
    });
  };

  const isThalysUser = user?.offices?.filter((office) => THALYS_OFFICES.includes(office.name))?.length > 0;

  const exchangeBookingLegs = [
    ...(buildFaresBeingExchanged(outboundLegs, bookingItemRefs) ?? []),
    ...(buildFaresBeingExchanged(inboundLegs, bookingItemRefs) ?? [])
  ].flat();

  const exchangeBookingItems = exchangeBookingLegs?.map(({ products }) => products ?? [])?.flat();

  const { isAOPActivated } = getAOPStatus(exchangeBookingItems);
  const notAllPassengersSelected =
    (outbound.bookingItems.length && outbound.bookingItems.length !== passengers.length) ||
    (inbound.bookingItems.length && inbound.bookingItems.length !== passengers.length);
  const hasConnectingTrains = exchangeBookingLegs.some(
    ({ serviceType }) => serviceType?.code !== serviceTypeCodes.EUROSTAR && serviceType?.code !== serviceTypeCodes.GREENSPEED
  );
  const shouldOfferSwapStations = isUserEurostarAgent(user);
  const reasonsSwapStationsIsDisabled = getReasonsForDisablingSwapStations({
    isGroupBooking,
    hasConnectingTrains,
    isAOPActivated,
    isTwoWayJourneyOneWayExchange,
    notAllPassengersSelected
  });

  return (
    <ExchangeJourneySearch
      variables={searchArgs}
      isOnHoldOrProvisionalBooking={isOnHoldOrProvisionalBooking}
      isSwapStations={hasSwapStations}
      isOverrideRule={isOverrideRule}
      onCompleted={handleCompletedSearch}
    >
      {(searchData) => (
        <>
          <Row span={isTwoWayExchange({ outboundDate, inboundDate }) ? 16 : 12}>
            <ExchangeResults
              searchData={searchData}
              status={status}
              isGroupBooking={isGroupBooking}
              outboundDate={outboundDate}
              inboundDate={inboundDate}
              outbound={outbound}
              inbound={inbound}
              outboundSelectedFare={outboundSelectedFare}
              inboundSelectedFare={inboundSelectedFare}
              reference={reference}
              currency={exchangeCurrency}
              handleSelectFare={handleSelectFare}
              outboundJourneyInfo={outboundInfo}
              inboundJourneyInfo={inboundInfo}
              overrideReason={overrideReason}
              isStationAgent={user.isStation}
              stationName={user.paymentLocation && user.paymentLocation.name}
              outboundTrainNumbers={outboundTrainNumbers}
              inboundTrainNumbers={inboundTrainNumbers}
              isMember={isMember}
              activePoints={activePoints}
              swapStations={hasSwapStations}
              shouldForceOutbound={shouldForceOutbound}
              shouldForceInbound={shouldForceInbound}
              outboundExtras={outboundExtras}
              inboundExtras={inboundExtras}
              isMultiJourneyBooking={isMultiJourneyBooking}
              isMultiJourney={isMultiJourney}
              booking={booking}
              subscriptionStartDate={subscriptionStartDate || null}
              subscriptionEndDate={subscriptionEndDate || null}
              hasSpecialLoyaltyTier={hasSpecialLoyaltyTier}
              outboundLegsBeingExchanged={buildFaresBeingExchanged(outboundLegs, bookingItemRefs)}
              inboundLegsBeingExchanged={buildFaresBeingExchanged(inboundLegs, bookingItemRefs)}
              isExchangingASubsetOfPassengersOutbound={isExchangingASubsetOfPassengersOutbound}
              isExchangingASubsetOfPassengersInbound={isExchangingASubsetOfPassengersInbound}
              isExchangingSinglegOfAOutboundSummated={isExchangingSinglegOfAOutboundSummated}
              isExchangingMultipleLegsOfAOutboundSummated={isExchangingMultipleLegsOfAOutboundSummated}
              isExchangingSingleLegOfAInboundSummated={isExchangingSingleLegOfAInboundSummated}
              isExchangingMultipleLegsOfAInboundSummated={isExchangingMultipleLegsOfAInboundSummated}
              isSummatedFaresSelectionEnabled
              isExchangingOutboundDirectFare={isExchangingOutboundDirectFare}
              isExchangingOutboundThroughFare={isExchangingOutboundThroughFare}
              isExchangingInboundDirectFare={isExchangingInboundDirectFare}
              isExchangingInboundThroughFare={isExchangingInboundThroughFare}
              isThalysUser={isThalysUser}
              bookingItemRefs={bookingItemRefs}
              animalsPerTrain={animalsData}
              animalsPerTrainLoading={animalsLoading}
              assistancePerTrain={assistanceData}
              assistancePerTrainLoading={assistanceLoading}
              serviceDetailsData={serviceDetailsData}
              serviceDetailsLoading={serviceDetailsLoading}
            />
          </Row>
          {isOneWayExchange({ outboundDate, inboundDate }) && (
            <SearchContainer show={showBookingMagnet} data-cy="search-menu" span={4}>
              <SearchRequestBookingMagnet
                outbound={booking?.outbound}
                inbound={booking?.inbound}
                origin={getStationUic(origin)}
                destination={getStationUic(destination)}
                stations={stations}
                travelDates={{ outboundDate, inboundDate }}
                onSubmit={handleSearchChange}
                swapStations={hasSwapStations}
                shouldOfferSwapStations={shouldOfferSwapStations}
                reasonsSwapStationsIsDisabled={reasonsSwapStationsIsDisabled}
                subscriptionStartDate={subscriptionStartDate || null}
                subscriptionEndDate={subscriptionEndDate || null}
                bookingItems={bookingItems}
                isExchange
                isExchangingSinglegOfAOutboundSummated={isExchangingSinglegOfAOutboundSummated}
                isExchangingSingleLegOfAInboundSummated={isExchangingSingleLegOfAInboundSummated}
              />
            </SearchContainer>
          )}
          {isTwoWayExchange({ outboundDate, inboundDate }) && (
            <StyledModal isOpen={showBookingMagnet} onClose={toggleSearchMenu}>
              <SearchRequestBookingMagnet
                outbound={booking?.outbound}
                inbound={booking?.inbound}
                origin={getStationUic(origin)}
                destination={getStationUic(destination)}
                returningOrigin={getStationUic(returningOrigin)}
                returningDestination={getStationUic(returningDestination)}
                stations={stations}
                travelDates={{ outboundDate, inboundDate }}
                onSubmit={handleSearchChange}
                swapStations={hasSwapStations}
                shouldOfferSwapStations={shouldOfferSwapStations}
                reasonsSwapStationsIsDisabled={reasonsSwapStationsIsDisabled}
                subscriptionStartDate={subscriptionStartDate || null}
                subscriptionEndDate={subscriptionEndDate || null}
                isExchange
                bookingItems={bookingItems}
                isExchangingSinglegOfAOutboundSummated={isExchangingSinglegOfAOutboundSummated}
                isExchangingSingleLegOfAInboundSummated={isExchangingSingleLegOfAInboundSummated}
              />
            </StyledModal>
          )}
        </>
      )}
    </ExchangeJourneySearch>
  );
};

ExchangeJourneySelection.propTypes = {
  reference: PropTypes.string,
  overrideReason: PropTypes.string,
  exchangeCurrency: currencyShape,
  booking: bookingShape,
  history: historyShape,
  location: locationShape,
  outbound: legShape,
  inbound: legShape,
  stations: stationsShape,
  user: userShape,
  outboundTrainNumbers: PropTypes.arrayOf(PropTypes.string),
  inboundTrainNumbers: PropTypes.arrayOf(PropTypes.string),
  membershipNumber: PropTypes.string,
  isMember: PropTypes.bool,
  activePoints: PropTypes.number,
  hasSpecialLoyaltyTier: PropTypes.bool,
  showBookingMagnet: PropTypes.bool,
  setShowBookingMagnet: PropTypes.func,
  outboundExtras: PropTypes.arrayOf(productShape),
  inboundExtras: PropTypes.arrayOf(productShape),
  isMultiJourneyBooking: PropTypes.bool,
  bookingItemRefs: PropTypes.arrayOf(PropTypes.string),
  outboundLegs: PropTypes.arrayOf(legShape),
  inboundLegs: PropTypes.arrayOf(legShape)
};

ExchangeJourneySelection.defaultProps = {
  showBookingMagnet: false,
  outboundTrainNumbers: [],
  inboundTrainNumbers: []
};

export default ExchangeJourneySelection;
